import { assign, find, forEach, includes, isEmpty, remove } from 'lodash-es';

/*
 * A slightly extended interface of the attachment objects returned by the API
 * The following properties do not exist in the original object returned by the API:
 * errorMessage?: string; value is set if API returns error or front-end validation fails
 * isUploading?: boolean; set to true when file is uploading
 * file?: File; value is set when attachment object is generated
 * */
export interface IAptAttachment {
  createdBy: string;
  createdDate: number;
  effectiveDate?: null;
  errorMessage?: string;
  isUploading?: boolean;
  file?: File;
  fileId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  lastModifiedBy?: null;
  lastModifiedDate?: null;
  url?: string;
}

export class AptFileAttachmentsService {
  constructor(public api, public Upload: any, public $q, public $filter) {
    'ngInject';
  }

  findAttachmentByFileName(
    fileName: string,
    attachments: IAptAttachment[]
  ): IAptAttachment | undefined {
    return find(attachments, { fileName });
  }

  // This will return an attachment object either by returning an existing one
  // or generating a new one with the provided file name.
  // The assumption here is that attachment have unique file names and
  // existing ones would be overwritten / updated.
  getAttachmentObject(fileName, attachments): IAptAttachment {
    let attachment =
      this.findAttachmentByFileName(fileName, attachments) ||
      ({} as IAptAttachment);

    if (isEmpty(attachment)) {
      attachment.fileName = fileName;
      attachments.push(attachment);
    }

    return attachment;
  }

  getFileValidationMessage(file, acceptableExtensions) {
    const fileExtension = this.fileExtensionFilter(file.name);
    return includes(acceptableExtensions, fileExtension)
      ? ''
      : 'Invalid file format';
  }

  fileExtensionFilter(fileName: string) {
    return this.$filter('fileExtension')(fileName);
  }

  /*
   * @resourceId: number - rfpId, offerId, etc.
   * */
  handleFileSelection({
    files,
    currentAttachments = [] as IAptAttachment[],
    accountEmail,
    resourceId,
    acceptableExtensions
  }: any) {
    if (files.length) {
      let attachmentsToUpload: IAptAttachment[] = [];

      forEach(files as FileList, (file: File) => {
        // get existing attachment or new one
        let attachment: IAptAttachment = this.getAttachmentObject(
          file.name,
          currentAttachments
        );

        const errorMessage = this.getFileValidationMessage(
          file,
          acceptableExtensions
        );

        assign(attachment, {
          createdDate: attachment.createdDate || Date.now(),
          createdBy: attachment.createdBy || accountEmail,
          lastModifiedDate: Date.now(),
          lastModifiedBy: accountEmail,
          errorMessage,
          fileName: file.name,
          fileSize: file.size,
          fileType: this.fileExtensionFilter(file.name),
          file
        });

        // If we don't have a validation message for this file
        // then push into the array of attachments to upload
        if (!errorMessage) {
          attachmentsToUpload.push(attachment);
        }
      });

      forEach(attachmentsToUpload, (attachment) => {
        this.handleUpload(resourceId, attachment);
      });
    }
  }

  private handleUpload(resourceId, attachment: IAptAttachment) {
    const suppressAction = true; // prevent interceptor from handling HTTP error - in file upload components - we generally want true

    attachment.isUploading = true;

    // We're running this on a single file at a time, but we need to pass this an array of files
    let uploadLoader = this.uploadAttachments(
      resourceId,
      [attachment.file],
      suppressAction
    );

    return uploadLoader
      .then((response) => {
        attachment.fileId = response[0].fileId;
        attachment.url = response[0].url;
        attachment.fileName = response[0].fileName;
        attachment.fileType = response[0].fileType;
        attachment.isUploading = false;
      })
      .catch((response) => {
        attachment.errorMessage = response.message;
        attachment.isUploading = false;
      });
  }

  handleDelete(attachment: IAptAttachment, resourceId, currentAttachments) {
    console.assert(resourceId, 'resourceId required');

    const deferred = this.$q.defer();
    const suppressAction = true; // prevent interceptor from handling HTTP error - in file upload components - we generally want true

    // TODO: change isUploading name to something more generic.
    attachment.isUploading = true;

    if (attachment.fileId) {
      // fileId exists so this is an existing attachment so we need to make an API call
      this.deleteAttachment(resourceId, attachment.fileId, suppressAction)
        .then(() => {
          deferred.resolve([attachment.fileName, currentAttachments]);
        })
        .catch((response) => {
          deferred.reject(response.message);
        });
    } else {
      // file only exists locally so resolve the promise so we can remove it
      deferred.resolve([attachment.fileName, currentAttachments]);
    }

    return deferred.promise
      .then(([fileName, currentAttachments]) => {
        // update existing attachments array
        remove(currentAttachments, { fileName });
      })
      .catch((errorMessage) => {
        attachment.errorMessage = errorMessage;
        attachment.isUploading = false;
      });
  }

  /*
   * Make API call to upload attachment
   * NOTE: By default we are targeting the RFP attachments
   * @resourceId: number - rfpId, offerId, etc.
   * @files An Array of file objects - NOTE: not same as FileList object
   * @suppressAction: prevent interceptor from handling HTTP error so contextual message can be displayed
   * override this for custom upload API calls
   * */
  uploadAttachments(resourceId: number, files: File[], suppressAction) {
    return this.api.rfps.uploadAttachments(
      resourceId,
      this.Upload,
      files,
      suppressAction
    );
  }

  /*
   * Make API call to delete attachment
   * NOTE: By default we are targeting the RFP attachments
   * @resourceId: number - rfpId, offerId, etc.
   * @fileId: string - Id designated by backend
   * @suppressAction: prevent interceptor from handling HTTP error so contextual message can be displayed
   * override this for custom delete API calls
   * */
  deleteAttachment(resourceId: number, fileId: string, suppressAction) {
    return this.api.rfps.deleteAttachments(resourceId, fileId, suppressAction);
  }
}
